import { useCurrency } from '@/shared/context/CurrencyContext';
import { formatAmountInput, parseAmount } from '@/utils/currency';
import { CURRENCY_LIST } from '@/shared/types/currency.types';
import type { CurrencyCode } from '@/shared/types/currency.types';

interface InputAmountProps {
  value: number;
  onChange: (amount: number) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  showCurrencySelector?: boolean;
  currency?: CurrencyCode;
  onCurrencyChange?: (code: CurrencyCode) => void;
  className?: string;
}

export function InputAmount({
  value,
  onChange,
  label,
  placeholder = '0',
  disabled = false,
  error,
  showCurrencySelector = false,
  currency: currencyProp,
  onCurrencyChange,
  className = '',
}: InputAmountProps) {
  const { currency: ctxCurrency } = useCurrency();
  const currency = currencyProp ?? ctxCurrency;
  const config = CURRENCY_LIST.find((c) => c.code === currency)!;

  const handleChange = (raw: string) => {
    onChange(parseAmount(raw));
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      )}
      <div className={`flex rounded-xl border overflow-hidden ${error ? 'border-rose-300' : 'border-slate-200'}`}>
        {showCurrencySelector && onCurrencyChange ? (
          <select
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value as CurrencyCode)}
            disabled={disabled}
            className="px-2 py-2.5 bg-slate-50 border-r border-slate-200 text-xs font-semibold text-slate-600 focus:outline-none"
          >
            {CURRENCY_LIST.map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </select>
        ) : (
          <span className="px-3 py-2.5 bg-slate-50 border-r border-slate-200 text-xs font-semibold text-slate-500 shrink-0">
            {config.symbol}
          </span>
        )}
        <input
          type="text"
          inputMode="decimal"
          disabled={disabled}
          value={value ? formatAmountInput(value, currency) : ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2.5 text-sm text-slate-800 focus:outline-none disabled:opacity-50"
        />
      </div>
      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
    </div>
  );
}
